import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Save,
  Close,
  Delete,
  AutoAwesome,
  Refresh
} from "@mui/icons-material";
import { Button } from "common/components";
import Select from "common/components/base/select/Select";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
import { MainRoutes } from "../../../../../common/config";
import { ConfigurationHubService } from "../../../data/services/configuration-hub.service";
import {
  TagDefinition,
  DropdownOption
} from "../../../data/models/configuration-hub.model";
import "./TagEnrichmentPage.scss";

interface TagEnrichmentPhaseProps {
  tags: TagDefinition[];
  onTagsChange: (tags: TagDefinition[]) => void;
  onPrevious: () => void;
  onNext: () => void;
  onRefresh?: () => void;
  disabled?: boolean;
  configName: string;
  categoryName: string;
  categoryDescription: string;
  cfihosMasterObjectId: number;
  totalTags?: number;
}

const TagEnrichmentPhase: React.FC<TagEnrichmentPhaseProps> = ({
  tags,
  onTagsChange,
  onPrevious,
  onNext,
  onRefresh,
  disabled = false,
  configName,
  categoryName,
  categoryDescription,
  cfihosMasterObjectId,
  totalTags
}) => {
  const navigate = useNavigate();
  // Local state for editing
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingDefinition, setEditingDefinition] = useState<string>("");
  const [enrichingTagId, setEnrichingTagId] = useState<string | null>(null);

  // State for bulk operations
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [bulkEnriching, setBulkEnriching] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const [savingTagId, setSavingTagId] = useState<string | null>(null);
  const [processingEmbedding, setProcessingEmbedding] =
    useState<boolean>(false);

  // Get unique parent tags for dropdown
  const getAvailableParentTags = useCallback((): DropdownOption[] => {
    const parentTags = new Set(tags.map((tag) => tag.parentTagName));
    return Array.from(parentTags).map((parentTag) => ({
      value: parentTag,
      label: parentTag
    }));
  }, [tags]);

  // Checkbox handling functions
  const handleSelectTag = (tagId: string) => {
    const newSelected = new Set(selectedTagIds);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTagIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTagIds.size === tags.length) {
      setSelectedTagIds(new Set());
    } else {
      setSelectedTagIds(new Set(tags.map((tag) => tag.id)));
    }
  };

  const handleBulkEnrich = async () => {
    if (selectedTagIds.size === 0) return;

    setBulkEnriching(true);

    try {
      // Convert selected tag IDs to guidCodes for API payload
      const selectedGuidCodes = Array.from(selectedTagIds)
        .map((tagId) => {
          const tag = tags.find((t) => t.id === tagId);
          return tag?.guidCode;
        })
        .filter((guidCode): guidCode is string => !!guidCode); // Type guard to ensure only strings

      if (selectedGuidCodes.length === 0) {
        throw new Error("No valid guidCodes found for selected tags");
      }

      // Call the tag definition enrichment API
      const result = await ConfigurationHubService.enrichTagDefinitions(
        selectedGuidCodes
      );

      if (!result.error && result.data) {
        // If API call successful, clear selection and show success message
        setSelectedTagIds(new Set());

        ToastMessageUtil.show(
          `${selectedTagIds.size} tag${
            selectedTagIds.size > 1 ? "s" : ""
          } sent for enrichment successfully`,
          ToastVariant.SUCCESS
        );

        // Optionally refresh the tags to get the enriched definitions
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        const errorMessage = result.error?.data?.[0] || "Enrichment failed";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Tag enrichment error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      ToastMessageUtil.show(
        `Failed to enrich tags: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setBulkEnriching(false);
    }
  };

  const handleEnrichWithLLM = (tagId: string) => {
    setEnrichingTagId(tagId);

    try {
      // TODO: Implement actual AI/LLM enrichment API call
      // For now, just append enrichment indicator
      const updatedTags = tags.map((tag) => {
        if (tag.id === tagId) {
          return {
            ...tag,
            tagDefinition: tag.tagDefinition + " (AI enriched)"
          };
        }
        return tag;
      });

      onTagsChange(updatedTags);
      ToastMessageUtil.show(
        "Tag definition enriched successfully",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      ToastMessageUtil.show(
        "Failed to enrich tag definition",
        ToastVariant.ERROR
      );
    } finally {
      setEnrichingTagId(null);
    }
  };

  const handleStartEdit = (tag: TagDefinition) => {
    setEditingTagId(tag.id);
    setEditingDefinition(tag.tagDefinition);
  };

  const handleSaveEdit = async (tagId: string) => {
    if (!editingDefinition.trim()) {
      ToastMessageUtil.show(
        "Tag definition cannot be empty",
        ToastVariant.WARNING
      );
      return;
    }

    // Find the tag to get its guidCode
    const tagToUpdate = tags.find((tag) => tag.id === tagId);
    if (!tagToUpdate) {
      ToastMessageUtil.show("Tag not found", ToastVariant.ERROR);
      return;
    }

    setSavingTagId(tagId);

    try {
      // Call the PUT API endpoint
      const result = await ConfigurationHubService.updateTagEnrichment(
        tagToUpdate.guidCode,
        editingDefinition
      );

      if (!result.error) {
        // If API call successful, update the local state
        const updatedTags = tags.map((tag) =>
          tag.id === tagId
            ? {
                ...tag,
                tagDefinition: editingDefinition
              }
            : tag
        );

        onTagsChange(updatedTags);
        setEditingTagId(null);
        setEditingDefinition("");
        ToastMessageUtil.show("Tag updated successfully", ToastVariant.SUCCESS);
      } else {
        const errorMessage = result.error?.data?.[0] || "Failed to update tag";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Tag update error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      ToastMessageUtil.show(
        `Failed to update tag: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setSavingTagId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditingDefinition("");
  };

  const handleDeleteTag = async (tagId: string) => {
    // Find the tag to get its guidCode
    const tagToDelete = tags.find((tag) => tag.id === tagId);
    if (!tagToDelete) {
      ToastMessageUtil.show("Tag not found", ToastVariant.ERROR);
      return;
    }

    setDeletingTagId(tagId);

    try {
      // Call the DELETE API endpoint
      const result = await ConfigurationHubService.deleteTagEnrichment(
        tagToDelete.guidCode
      );

      if (!result.error) {
        // If API call successful, remove the tag from local state
        const updatedTags = tags.filter((tag) => tag.id !== tagId);
        onTagsChange(updatedTags);

        // Clear selection if the deleted tag was selected
        if (selectedTagIds.has(tagId)) {
          const newSelected = new Set(selectedTagIds);
          newSelected.delete(tagId);
          setSelectedTagIds(newSelected);
        }

        ToastMessageUtil.show("Tag deleted successfully", ToastVariant.SUCCESS);
      } else {
        const errorMessage = result.error?.data?.[0] || "Failed to delete tag";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Tag deletion error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      ToastMessageUtil.show(
        `Failed to delete tag: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setDeletingTagId(null);
    }
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;

    setRefreshing(true);

    try {
      await onRefresh();
      ToastMessageUtil.show(
        "Tags refreshed successfully",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Refresh error:", error);
      ToastMessageUtil.show("Failed to refresh tags", ToastVariant.ERROR);
    } finally {
      setRefreshing(false);
    }
  };

  const isActionDisabled =
    disabled ||
    editingTagId !== null ||
    enrichingTagId !== null ||
    bulkEnriching ||
    refreshing ||
    deletingTagId !== null ||
    savingTagId !== null ||
    processingEmbedding;

  const handleProcessForEmbedding = async () => {
    setProcessingEmbedding(true);

    try {
      // Call the Process Embedding API
      const result = await ConfigurationHubService.processEmbedding({
        masterObjectId: cfihosMasterObjectId
      });

      if (!result.error) {
        ToastMessageUtil.show(
          "Tags Enrichment completed successfully",
          ToastVariant.SUCCESS
        );

        // Navigate to completion page after successful API call
        navigate(MainRoutes.COMPLETE_TAG_LIBRARY_CONFIG, {
          state: {
            configName,
            categoryName,
            categoryDescription,
            tags,
            cfihosMasterObjectId
          }
        });
      } else {
        const errorMessage =
          result.error?.data?.[0] || "Processing embedding failed";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Process embedding error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      ToastMessageUtil.show(
        `Failed to process embedding: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setProcessingEmbedding(false);
    }
  };

  return (
    <div className="config-card">
      <div className="card-header">
        <h2>Enrich Tag Definitions</h2>
        <p className="card-subtitle">
          Use AI to enrich tag definitions or manually edit them. Delete
          unwanted tags before proceeding.
        </p>
      </div>

      <div className="tags-section">
        <div className="stats-row">
          <div className="stat-item">
            <div className="stat-label">Total Tags</div>
            <div className="stat-value">{totalTags || tags.length}</div>
          </div>
          {/* <div className="stat-item">
            <div className="stat-label">Category Type</div>
            <div className="stat-value">Discipline</div>
          </div> */}
          <div className="stat-item refresh-section">
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={isActionDisabled}
              title="Refresh tags data"
            >
              {refreshing ? (
                <>
                  <div className="spinner" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <Refresh />
                  <span>Refresh</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="tags-table">
          {/* Bulk Actions Toolbar */}
          {selectedTagIds.size > 0 && (
            <div className="bulk-actions-toolbar">
              <div className="selected-info">
                <span>
                  {selectedTagIds.size} tag{selectedTagIds.size > 1 ? "s" : ""}{" "}
                  selected
                </span>
              </div>
              <div className="bulk-actions">
                <button
                  className="bulk-action-btn enrich-bulk"
                  onClick={handleBulkEnrich}
                  disabled={bulkEnriching || enrichingTagId !== null}
                  title={`Enrich ${selectedTagIds.size} selected tag${
                    selectedTagIds.size > 1 ? "s" : ""
                  }`}
                >
                  {bulkEnriching ? (
                    <>
                      <div className="spinner" />
                      <span>Enriching...</span>
                    </>
                  ) : (
                    <>
                      <AutoAwesome />
                      <span>Enrich Selected ({selectedTagIds.size})</span>
                    </>
                  )}
                </button>
                <button
                  className="bulk-action-btn clear-selection"
                  onClick={() => setSelectedTagIds(new Set())}
                  title="Clear selection"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          <div className="table-header">
            <div className="select-column">
              <input
                type="checkbox"
                checked={selectedTagIds.size === tags.length && tags.length > 0}
                onChange={handleSelectAll}
                className="select-checkbox header-checkbox"
                title="Select all tags for Enrichment"
              />
            </div>
            <div>Tag Name</div>
            <div>Parent Tag</div>
            <div>Tag Definition</div>
            <div>Tag ID</div>
            <div>Actions</div>
          </div>

          <div
            className={`table-body ${
              (totalTags || tags.length) > 10 ? "scrollable" : ""
            }`}
          >
            {tags.map((tag) => (
              <div
                key={tag.id}
                className={`table-row ${
                  editingTagId === tag.id ? "editing" : ""
                } ${selectedTagIds.has(tag.id) ? "selected" : ""}`}
              >
                <div className="cell select-column">
                  <input
                    type="checkbox"
                    checked={selectedTagIds.has(tag.id)}
                    onChange={() => handleSelectTag(tag.id)}
                    className="select-checkbox"
                    title="Select tag for Enrichment"
                    disabled={
                      editingTagId === tag.id ||
                      enrichingTagId === tag.id ||
                      bulkEnriching ||
                      deletingTagId === tag.id ||
                      savingTagId === tag.id
                    }
                  />
                </div>
                <div className="cell tag-name">{tag.tagName}</div>
                <div className="cell parent-tag">
                  <span>{tag.parentTagName}</span>
                </div>
                <div className="cell tag-definition">
                  {editingTagId === tag.id ? (
                    <textarea
                      className="edit-textarea"
                      value={editingDefinition}
                      onChange={(e) => setEditingDefinition(e.target.value)}
                      placeholder="Enter tag definition..."
                    />
                  ) : (
                    <span>{tag.tagDefinition}</span>
                  )}
                </div>
                <div className="cell tag-id">
                  <span className="tag-id-badge">{tag.cfihosTagId}</span>
                </div>
                <div className="cell actions">
                  {editingTagId === tag.id ? (
                    <>
                      <button
                        className="action-button save-btn"
                        onClick={() => handleSaveEdit(tag.id)}
                        title="Save changes"
                        disabled={savingTagId !== null}
                      >
                        {savingTagId === tag.id ? (
                          <div className="spinner" />
                        ) : (
                          <Save />
                        )}
                      </button>
                      <button
                        className="action-button cancel-btn"
                        onClick={handleCancelEdit}
                        title="Cancel edit"
                      >
                        <Close />
                      </button>
                    </>
                  ) : (
                    <>
                      {/* <button
                      className="action-button enrich-btn"
                      onClick={() => handleEnrichWithLLM(tag.id)}
                      disabled={
                        enrichingTagId !== null ||
                        bulkEnriching ||
                        selectedTagIds.size > 0
                      }
                      title="Enrich with AI"
                    >
                      {enrichingTagId === tag.id ? (
                        <div className="spinner" />
                      ) : (
                        <AutoAwesome />
                      )}
                    </button> */}
                      <button
                        className="action-button edit-btn"
                        onClick={() => handleStartEdit(tag)}
                        disabled={
                          bulkEnriching ||
                          enrichingTagId !== null ||
                          savingTagId !== null ||
                          deletingTagId !== null
                        }
                        title="Edit tag"
                      >
                        <Edit />
                      </button>
                      <button
                        className="action-button delete-btn"
                        onClick={() => handleDeleteTag(tag.id)}
                        disabled={
                          bulkEnriching ||
                          enrichingTagId !== null ||
                          deletingTagId !== null ||
                          editingTagId !== null
                        }
                        title="Delete tag"
                      >
                        {deletingTagId === tag.id ? (
                          <div className="spinner" />
                        ) : (
                          <Delete />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-actions">
        {/* <div className="action-left">
          <Button
            variant="secondary"
            onClick={onPrevious}
            disabled={isActionDisabled}
          >
            Previous
          </Button>
        </div> */}
        <div className="action-right">
          <Button
            variant="primary"
            onClick={handleProcessForEmbedding}
            disabled={isActionDisabled || tags.length === 0}
          >
            {processingEmbedding ? (
              <>
                <div className="spinner" />
                Processing Configuration...
              </>
            ) : (
              "Complete Configuration"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TagEnrichmentPhase;
